import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api } from '../api/client.js';
import { ChartCard, RangePicker } from '../components/ChartCard.jsx';
import { Delta } from '../components/Delta.jsx';
import { LineChart } from '../components/LineChart.jsx';
import { ErrorState, LoadingState, StaleNotice } from '../components/States.jsx';
import { TradeTicket } from '../components/TradeTicket.jsx';
import { useLiveQuery } from '../hooks/useLiveQuery.js';
import {
  clockTime,
  compactNumber,
  marketCap,
  mediumDate,
  money,
  percent,
  ratio,
  shares,
  signedMoney,
  signedPercent,
} from '../lib/format.js';

const RANGES = ['1D', '5D', '1M', '3M', '1Y'];

export function InstrumentPage() {
  const { symbol } = useParams();
  const [range, setRange] = useState('1D');
  // null when closed; otherwise the side the ticket opens on.
  const [ticketSide, setTicketSide] = useState(null);

  const quote = useLiveQuery((signal) => api.instrument(symbol, signal), {
    intervalMs: 4000,
    deps: [symbol],
  });
  const history = useLiveQuery((signal) => api.instrumentHistory(symbol, range, signal), {
    intervalMs: range === '1D' ? 15_000 : 0,
    deps: [symbol, range],
  });
  const portfolio = useLiveQuery((signal) => api.portfolio(signal), { intervalMs: 15_000 });

  // Both of these now fire only when there is genuinely nothing to show. A refresh
  // that fails once the page has loaded no longer sets `error`, so a single blip on
  // the poll cannot throw away the whole page — including the range the reader
  // selected, which this early return used to discard along with everything else.
  if (quote.isLoading) return <LoadingState label={`Loading ${symbol}`} height={360} />;
  if (quote.error) return <ErrorState error={quote.error} onRetry={quote.refresh} title={`Could not load ${symbol}`} />;

  const data = quote.data;
  const position = (portfolio.data?.positions ?? []).find((entry) => entry.symbol === data.symbol);

  return (
    <>
      {quote.refreshError && (
        <StaleNotice
          error={quote.refreshError}
          since={quote.updatedAt ? clockTime(quote.updatedAt) : null}
          onRetry={quote.refresh}
        />
      )}
      <p className="card__subtitle" style={{ marginBottom: 12 }}>
        <Link to="/markets">← Markets</Link>
      </p>

      <div className="detailhead">
        <div style={{ marginRight: 'auto' }}>
          <h1>
            {data.symbol} <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>· {data.name}</span>
          </h1>
          <p className="card__subtitle">
            {[data.industry, data.sector, data.exchange, data.currency].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div>
          <div className="detailhead__price">{money(data.price)}</div>
          <div style={{ marginTop: 4 }}>
            <Delta value={data.change}>
              {signedMoney(data.change)} ({signedPercent(data.changePercent)})
            </Delta>
            <span style={{ color: 'var(--text-muted)', fontSize: 12, marginLeft: 8 }}>
              as of {clockTime(data.updatedAt)}
            </span>
          </div>
        </div>

        <div className="tradebar">
          <button type="button" className="button" onClick={() => setTicketSide('BUY')}>
            Buy
          </button>
          {/* Selling nothing is not an error worth explaining in a dialog, so the
              control is simply unavailable until there is a position. */}
          <button
            type="button"
            className="button button--ghost"
            onClick={() => setTicketSide('SELL')}
            disabled={!position}
            title={position ? undefined : `You do not hold ${data.symbol}`}
          >
            Sell
          </button>
        </div>
      </div>

      {ticketSide && (
        <TradeTicket
          symbol={data.symbol}
          side={ticketSide}
          onClose={() => setTicketSide(null)}
          onFilled={() => {
            // The holding, the cash, and the tape all moved. Refetch rather than
            // patching local state, so what is shown is what the API says.
            quote.refresh?.();
            portfolio.refresh?.();
          }}
        />
      )}

      {position && <PositionBanner position={position} />}

      <div style={{ marginBottom: 16 }}>
        <PriceChartCard query={history} range={range} onRangeChange={setRange} symbol={data.symbol} />
      </div>

      <section className="card">
        <header className="card__head">
          <div className="card__titles">
            <h2 className="card__title">Key statistics</h2>
            <p className="card__subtitle">
              Every figure here is generated by the demo — none of it is {data.name}'s.
            </p>
          </div>
        </header>
        <div className="card__body">
          <dl className="deflist">
            <div>
              <dt>Open</dt>
              <dd>{money(data.open)}</dd>
            </div>
            <div>
              <dt>Previous close</dt>
              <dd>{money(data.previousClose)}</dd>
            </div>
            <div>
              <dt>Day range</dt>
              <dd>
                {money(data.dayLow)} – {money(data.dayHigh)}
              </dd>
            </div>
            <div>
              <dt>52-week range</dt>
              <dd>
                {money(data.fiftyTwoWeekLow)} – {money(data.fiftyTwoWeekHigh)}
              </dd>
            </div>
            <div>
              <dt>Bid / ask</dt>
              <dd>
                {money(data.bid)} / {money(data.ask)}
              </dd>
            </div>
            <div>
              <dt>Volume</dt>
              <dd>{compactNumber(data.volume)}</dd>
            </div>
            <div>
              <dt>Average volume</dt>
              <dd>{compactNumber(data.avgVolume)}</dd>
            </div>
            <div>
              <dt>Market cap</dt>
              <dd>{marketCap(data.marketCapB)}</dd>
            </div>
            <div>
              <dt>P/E ratio</dt>
              <dd>{data.peRatio === null ? 'n/a' : ratio(data.peRatio)}</dd>
            </div>
            <div>
              <dt>Beta</dt>
              <dd>{ratio(data.beta)}</dd>
            </div>
            <div>
              <dt>Dividend yield</dt>
              <dd>{data.dividendYield ? percent(data.dividendYield * 100) : 'None'}</dd>
            </div>
            <div>
              <dt>Spread</dt>
              <dd>{money(data.spread)}</dd>
            </div>
          </dl>
        </div>
      </section>
    </>
  );
}

function PositionBanner({ position }) {
  return (
    <section className="card" style={{ marginBottom: 16 }}>
      <div className="card__body" style={{ paddingTop: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28, alignItems: 'baseline' }}>
          <div>
            <div className="tile__label">You hold</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>
              {shares(position.quantity)} shares · {money(position.marketValue, { cents: false })}
            </div>
          </div>
          <div>
            <div className="tile__label">Average cost</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{money(position.averageCost)}</div>
          </div>
          <div>
            <div className="tile__label">Unrealized P/L</div>
            <div style={{ fontSize: 18 }}>
              <Delta value={position.unrealizedPnl}>
                {signedMoney(position.unrealizedPnl)} ({signedPercent(position.unrealizedPnlPercent)})
              </Delta>
            </div>
          </div>
          <div>
            <div className="tile__label">Opened</div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{mediumDate(position.openedAt)}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PriceChartCard({ query, range, onRangeChange, symbol }) {
  const bars = query.data?.bars ?? [];
  const points = useMemo(() => bars.map((bar) => ({ label: bar.t, value: bar.close })), [bars]);

  const label = (value) => (range === '1D' ? value : mediumDate(value));

  return (
    <ChartCard
      title={`${symbol} price`}
      subtitle={range === '1D' ? 'Today, 1-minute closes (ET)' : `${range} of daily closes`}
      actions={<RangePicker label="Price range" value={range} options={RANGES} onChange={onRangeChange} />}
      table={
        <div className="tablewrap">
          <table className="table">
            <caption>
              {range === '1D' ? '1-minute' : 'Daily'} bars for {symbol}.
            </caption>
            <thead>
              <tr>
                <th scope="col">{range === '1D' ? 'Time (ET)' : 'Date'}</th>
                <th scope="col">Open</th>
                <th scope="col">High</th>
                <th scope="col">Low</th>
                <th scope="col">Close</th>
                <th scope="col">Volume</th>
              </tr>
            </thead>
            <tbody>
              {[...bars].reverse().map((bar) => (
                <tr key={bar.t}>
                  <td>{label(bar.t)}</td>
                  <td>{money(bar.open)}</td>
                  <td>{money(bar.high)}</td>
                  <td>{money(bar.low)}</td>
                  <td>{money(bar.close)}</td>
                  <td>{compactNumber(bar.volume)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }
    >
      {query.isLoading ? (
        <LoadingState height={300} label={`Loading ${symbol} history`} />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={query.refresh} />
      ) : (
        <LineChart
          points={points}
          height={300}
          stale={query.isRefreshing}
          valueFormat={(value) => money(value)}
          labelFormat={label}
          ariaLabel={`${symbol} closing price over ${range}`}
        />
      )}
    </ChartCard>
  );
}
